// public/v3/src/components/complex-ccm-review-modal.jsx — pre-generation
// review for CPT 99487 (Complex Chronic Care Management) patients who have
// no new clinical data since their prior care plan.
//
// CMS requires 99487 to include establishment OR substantial revision of the
// comprehensive care plan per service period. When a patient has no new labs
// and no new office-visit notes since the prior plan, the substantial-
// revision requirement is at risk and the billing decision deserves an
// explicit operator choice — not AI prompt manipulation that produces
// thin-revision text the doctor must remember to scrutinize.
//
// The modal surfaces three decisions:
//   1. Proceed with 99487 (operator attests revision is documented elsewhere)
//   2. Downgrade to 99490 (clinical staff-led CCM)
//   3. Downgrade to 99491 (practitioner-led CCM)
// Cancel aborts the push entirely; the doctor handles offline.
//
// Persistence of decisions is handled by the caller (studio.jsx /
// push-modal.jsx) — this component is pure UI. onDecide receives
// `{ action: 'proceed'|'downgrade', newProgram?: '99490'|'99491' }`.

import React, { useState, useEffect } from 'react';
import { useUnsavedChangesGuard } from '../lib/hooks/use-unsaved-changes-guard';

function formatMonthLong(sinceDate) {
  // sinceDate is YYYY-MM-DD; the modal copy wants "April 2026".
  if (!sinceDate || typeof sinceDate !== 'string') return '';
  const m = sinceDate.match(/^(\d{4})-(\d{2})/);
  if (!m) return '';
  const y = Number(m[1]);
  const mo = Number(m[2]);
  if (!Number.isFinite(y) || !Number.isFinite(mo)) return '';
  const d = new Date(y, mo - 1, 1);
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function formatMonthLongFromKey(monthKey) {
  if (!monthKey || typeof monthKey !== 'string') return '';
  const m = monthKey.match(/^(\d{4})-(\d{2})/);
  if (!m) return '';
  const y = Number(m[1]);
  const mo = Number(m[2]);
  if (!Number.isFinite(y) || !Number.isFinite(mo)) return '';
  const d = new Date(y, mo - 1, 1);
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

const OPTIONS = [
  {
    value: 'proceed',
    label: 'Proceed with 99487',
    detail: 'Substantial revision will be documented by the practitioner from sources outside the chart (e.g., MDM during a verbal review).',
  },
  {
    value: 'downgrade-99490',
    label: 'Downgrade to 99490 (clinical staff-led CCM)',
    detail: '99490 has no substantial-revision requirement. Patient program will change permanently until reverted.',
  },
  {
    value: 'downgrade-99491',
    label: 'Downgrade to 99491 (practitioner-led CCM)',
    detail: '99491 has no substantial-revision requirement. Patient program will change permanently until reverted.',
  },
];

export function ComplexCcmReviewModal({ patient, monthStr, eligibility, onClose, onDecide, busy }) {
  const [choice, setChoice] = useState('proceed');

  const { attemptDismiss, showDiscardPrompt, confirmDiscard, keepEditing } =
    useUnsavedChangesGuard({ values: { choice }, onClose, busy });

  // Esc key dismissal
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') attemptDismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [attemptDismiss]);

  const raw = patient?._raw || patient || {};
  const name = ((raw.fname || '') + ' ' + (raw.lname || '')).trim() || patient?.name || 'this patient';
  const priorPlanMonth = formatMonthLong(eligibility?.sinceDate);
  const currentMonth = formatMonthLongFromKey(monthStr);

  function handleApply() {
    if (busy) return;
    if (choice === 'proceed') {
      onDecide({ action: 'proceed' });
    } else if (choice === 'downgrade-99490') {
      onDecide({ action: 'downgrade', newProgram: '99490' });
    } else if (choice === 'downgrade-99491') {
      onDecide({ action: 'downgrade', newProgram: '99491' });
    }
  }

  return (
    <div className="modal-backdrop" onClick={attemptDismiss}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Review CPT 99487 for ${name}`}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 560 }}>
        <div className="modal-head">
          <h3>Review CPT 99487 for {name}{currentMonth ? ` — ${currentMonth}` : ''}</h3>
        </div>
        {showDiscardPrompt ? (
          <>
            <div className="modal-body">
              <h3 style={{ marginTop: 0 }}>Discard review?</h3>
              <p>You've made changes but haven't saved yet.</p>
            </div>
            <div className="modal-foot">
              <button className="btn" onClick={keepEditing}>Keep editing</button>
              <button className="btn warn" onClick={confirmDiscard}>Discard</button>
            </div>
          </>
        ) : (
          <>
            <div className="modal-body">
              <p style={{ marginTop: 0 }}>
                This patient is enrolled in <b>99487 (Complex CCM)</b>, which CMS requires to include
                establishment or substantial revision of the comprehensive care plan during each
                service period.
              </p>
              <p>
                No new labs or office-visit notes have been added to this patient&apos;s chart since their
                prior care plan{priorPlanMonth ? ` (${priorPlanMonth})` : ''}. Without new clinical data,
                the substantial-revision requirement may not be met for this billing month.
              </p>
              <p style={{ fontWeight: 500, marginBottom: 8 }}>How would you like to proceed?</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    style={{
                      display: 'flex',
                      gap: 10,
                      alignItems: 'flex-start',
                      padding: '10px 12px',
                      border: '1px solid var(--line)',
                      borderRadius: 'var(--r)',
                      background: choice === opt.value ? 'var(--surface-2)' : 'var(--surface)',
                      cursor: busy ? 'wait' : 'pointer',
                    }}>
                    <input
                      type="radio"
                      name="ccm-99487-decision"
                      value={opt.value}
                      checked={choice === opt.value}
                      disabled={busy}
                      onChange={() => setChoice(opt.value)}
                      style={{ marginTop: 3 }}
                    />
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 14, color: 'var(--ink-1)' }}>{opt.label}</div>
                      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>{opt.detail}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div className="modal-foot">
              <button className="btn" onClick={attemptDismiss} disabled={busy}>Cancel</button>
              <button className="btn primary" onClick={handleApply} disabled={busy}>
                {busy ? 'Applying…' : 'Apply & Generate'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
